import produce, { castDraft } from 'immer';
import { Dispatch, AnyAction } from 'redux';
import { typedAction } from './common/actions';
import { RootState } from '../redux/reducers';
import { TintWrapper } from '../services/tint-wrapper';
import Logger from '../../../shared/utils/logger';
import { IPost, IPostLinks, IPostWithCTA, Reply } from '../../../shared/types/Post';
import { ICta, ICtaAssociation, IPostRelationships } from '../../../shared/types/PostRelationships';
import { IGetPostsReturnValue, IQueryParamFilters } from '../../../shared/services/api.service';
import { IProduct, IProductTags } from '../../../shared/types/Product';
import { Nullable } from '../../../shared/types/Typescript-utils';

export enum PostsStatus {
  FETCHING = 'POSTS_FETCHING',
  SUCCESS = 'POSTS_SUCCESS',
  ERROR = 'POSTS_ERROR',
  REMOVED_WEBSOCKETS = 'REMOVED_WEBSOCKETS',
  NEW_POSTS_WEBSOCKETS = 'NEW_POSTS_WEBSOCKETS',
  APPROVE_POSTS_WEBSOCKETS = 'APPROVE_POSTS_WEBSOCKETS',
  PUBLIC_POST_SUBMITTED = 'PUBLIC_POST_SUBMITTED',
  UPDATE_POST_POLLING = 'UPDATE_POST_POLLING',
}

type Page = 'next' | 'prev';

interface PostsData {
  posts: readonly IPost[];
  postsRelationships?: readonly IPostRelationships[];
  links: IPostLinks;
}

export interface IProductsData {
  productTags: readonly IProductTags[];
  productList: readonly IProduct[];
}

export interface PostsState {
  data: PostsData[];
  error: Nullable<Error>;
  status: Nullable<PostsStatus>;
  isPagination: boolean;
  page?: Page;
  currentFilters?: IQueryParamFilters;
  products: IProductsData;
  isRemoveDuplicatePosts?: boolean;
}

const initialState: PostsState = {
  data: [],
  error: null,
  status: null,
  isPagination: false,
  page: undefined,
  currentFilters: undefined,
  isRemoveDuplicatePosts: undefined,
  products: {
    productTags: [],
    productList: [],
  },
};

export const FETCHING = 'posts/fetching';
export const FETCHING_SUCCESS = 'posts/success';
export const FETCHING_ERROR = 'posts/error';
export const EMPTY_POSTS = 'posts/empty_posts';
export const REMOVED_POSTS_WEBSOCKET = 'posts/remove_posts_websocket';
export const NEW_POSTS_WEBSOCKET = 'posts/add_posts_websocket';
export const APPROVE_POSTS_WEBSOCKET = 'posts/approve_posts_websocket';
export const PUBLIC_POST_SUBMITTED = 'posts/public_post_submitted';
export const UPDATE_POST_POLLING = 'posts/update_post_polling';

export const fetchPosts = (isPagination = false, page?: Page) => {
  return typedAction(FETCHING, { isPagination, page });
};

export const fetchPostsSuccess = (payload: {
  posts: ReadonlyArray<IPost>;
  postsRelationships?: ReadonlyArray<IPostRelationships>;
  links: IPostLinks;
  currentFilters?: IQueryParamFilters;
  isRemoveDuplicatePosts?: boolean;
}) => {
  return typedAction(FETCHING_SUCCESS, payload);
};

export const fetchPostsError = (payload: Error) => {
  Logger.warning(payload);
  return typedAction(FETCHING_ERROR, payload);
};

export const fetchNextPage = (isPagination = true) => {
  return fetchPage(isPagination, 'next');
};

export const fetchPrevPage = (isPagination = true) => {
  return fetchPage(isPagination, 'prev');
};

export const fetchPage = (isPagination: boolean, page: Page) => {
  return (dispatch: Dispatch<AnyAction>, getState: () => RootState) =>
    Promise.resolve().then(() => {
      const posts = getState().posts.data;
      const lastPostsSnapshot = posts[posts.length - 1];
      const pageLink = lastPostsSnapshot ? lastPostsSnapshot.links[page] : undefined;
      if (pageLink) {
        dispatch(fetchPosts(isPagination, page));
        return TintWrapper.getTint()
          .getPage(pageLink)
          .then(res => {
            if (!isPagination || res?.posts?.length !== 0) {
              dispatch(
                fetchPostsSuccess({
                  ...res,
                  currentFilters: getState().posts.currentFilters
                    ? (getState().posts.currentFilters as IQueryParamFilters)
                    : undefined,
                })
              );
            }
          })
          .catch(err => dispatch(fetchPostsError(err)));
      }
    });
};

export const getFilteredPosts = (filters: IQueryParamFilters) => {
  return TintWrapper.getTint().getPosts({
    ...filters,
  });
};

export const updatePostPolling = (payload: IGetPostsReturnValue) => {
  return typedAction(UPDATE_POST_POLLING, payload);
};

export const emptyPosts = () => {
  return typedAction(EMPTY_POSTS);
};

export const searchForPosts = (
  query?: IQueryParamFilters,
  isRemoveDuplicatePosts?: boolean,
  additionalIncludeParam?: string
) => {
  return (dispatch: Dispatch<AnyAction>) =>
    Promise.resolve().then(() => {
      dispatch(fetchPosts());
      return TintWrapper.getTint()
        .getPosts(query, additionalIncludeParam)
        .then(res => {
          dispatch(emptyPosts());
          return res;
        })
        .then(res =>
          dispatch(
            fetchPostsSuccess({
              ...res,
              currentFilters: query ? (query as IQueryParamFilters) : undefined,
              isRemoveDuplicatePosts,
            })
          )
        )
        .catch(err => dispatch(fetchPostsError(err)));
    });
};

export const removePostsWebsocket = (payload: string) => {
  return typedAction(REMOVED_POSTS_WEBSOCKET, payload);
};

export const newPostsWebsocket = (payload: number[]) => {
  return (dispatch: Dispatch<AnyAction>) =>
    Promise.resolve().then(() => {
      dispatch(fetchPosts());
      return TintWrapper.getTint()
        .getPosts({ social_feed_id: payload, size: 1 })
        .then(res => dispatch(newPostsWebsocketSuccess(res)))
        .catch(err => dispatch(fetchPostsError(err)));
    });
};

export const newPostsWebsocketSuccess = (payload: {
  posts: ReadonlyArray<IPost>;
  postsRelationships?: ReadonlyArray<IPostRelationships>;
  links: IPostLinks;
}) => {
  return typedAction(NEW_POSTS_WEBSOCKET, payload);
};

export const publicPostSubmission = ({
  socialFeedId,
  size = 1,
  tag,
  saved_filter_id,
  product_sku,
}: {
  socialFeedId: number[];
  size?: number;
  tag?: string;
  saved_filter_id?: number;
  product_sku?: string;
}) => {
  return (dispatch: Dispatch<AnyAction>) =>
    Promise.resolve().then(() => {
      dispatch(fetchPosts());
      return TintWrapper.getTint()
        .getPosts({ social_feed_id: socialFeedId, size: size, tag, saved_filter_id, product_sku })
        .then(res => dispatch(publicPostSubmissionSuccess(res)))
        .catch(err => dispatch(fetchPostsError(err)));
    });
};

export const publicPostSubmissionSuccess = (payload: {
  posts: ReadonlyArray<IPost>;
  postsRelationships?: ReadonlyArray<IPostRelationships>;
  links: IPostLinks;
}) => {
  return typedAction(PUBLIC_POST_SUBMITTED, payload);
};

export const approvePostsWebsocket = (payload: string) => {
  return (dispatch: Dispatch<AnyAction>) =>
    Promise.resolve().then(() => {
      dispatch(fetchPosts());
      return TintWrapper.getTint()
        .getSinglePost(payload)
        .then(res => dispatch(approvePostsWebsocketSuccess(res)))
        .catch(err => dispatch(fetchPostsError(err)));
    });
};

export const approvePostsWebsocketSuccess = (payload: {
  post: IPost;
  postsRelationships?: ReadonlyArray<IPostRelationships>;
  links: IPostLinks;
}) => {
  return typedAction(APPROVE_POSTS_WEBSOCKET, payload);
};

export type PostsAction = ReturnType<
  | typeof fetchPosts
  | typeof fetchPostsSuccess
  | typeof fetchPostsError
  | typeof emptyPosts
  | typeof removePostsWebsocket
  | typeof newPostsWebsocketSuccess
  | typeof approvePostsWebsocketSuccess
  | typeof publicPostSubmissionSuccess
  | typeof updatePostPolling
>;

// What happening here:
// So we should always check external id and attachment_id for all types of posts except instagram
// For instagram we should check externalId and post url
// If flag remove_duplicate_images is on we should also check image_phash
const mapPosts = (posts: IPost[], isRemoveDuplicatePosts?: boolean): IPost[] => {
  const postsWithoutDuplicates = uniqWithUrlAndExternalId(
    isRemoveDuplicatePosts ? uniqueWithPhash(posts) : uniqWithExternalId(posts)
  );

  return postsWithoutDuplicates.filter(post => {
    return new Date(post.attributes.published_at).getTime() < new Date().getTime();
  });
};

const handlePolledPosts = (
  posts: readonly IPost[],
  polledPosts: readonly IPost[],
  currentFilters?: IQueryParamFilters
) => {
  const type = currentFilters ? currentFilters.type : undefined;

  if (
    currentFilters &&
    ((currentFilters.social_feed_id && currentFilters.social_feed_id.length > 0) || currentFilters.q)
  ) {
    return posts;
  }
  return polledPosts.filter(post => (type ? post.attributes.type === type : true));
};

export function postsReducer(state = initialState, action: PostsAction): PostsState {
  switch (action.type) {
    case FETCHING:
      return {
        ...state,
        status: PostsStatus.FETCHING,
        isPagination: action.payload.isPagination,
        page: action.payload.page,
      };

    case FETCHING_SUCCESS:
      return {
        ...state,
        status: PostsStatus.SUCCESS,
        isRemoveDuplicatePosts:
          typeof action.payload.isRemoveDuplicatePosts !== 'undefined'
            ? action.payload.isRemoveDuplicatePosts
            : state.isRemoveDuplicatePosts,
        products: mapRelationshipsProducts(state.products, action.payload.postsRelationships),
        currentFilters: action.payload.currentFilters,
        data: state.isPagination
          ? [
              !action.payload.links.next || !action.payload.links.prev
                ? state.page === 'prev'
                  ? { ...action.payload, links: { ...state.data[0].links, next: state.data[0].links.self } }
                  : { ...action.payload, links: { ...state.data[0].links, prev: state.data[0].links.self } }
                : action.payload,
            ]
          : [
              ...state.data,
              {
                ...action.payload,
                posts: mapPosts(
                  action.payload.posts as IPost[],
                  typeof action.payload.isRemoveDuplicatePosts !== 'undefined'
                    ? action.payload.isRemoveDuplicatePosts
                    : state.isRemoveDuplicatePosts
                ).map(post => ({
                  ...post,
                  siblings: post.relationships.siblings?.data?.map(
                    sibling => action.payload?.postsRelationships?.find(e => e.id === sibling.id) as IPostWithCTA
                  ),
                  replies: post.relationships.replies?.data?.map(reply => {
                    const foundReply = action.payload?.postsRelationships
                      ?.filter(e => e.type === 'reply')
                      .find(e => e.id === reply.id);

                    return {
                      ...foundReply,
                    } as Reply;
                  }),
                })),
              },
            ],
      };
    case EMPTY_POSTS:
      return {
        ...state,
        data: [],
        error: null,
        isPagination: false,
      };
    case FETCHING_ERROR:
      return { ...state, status: PostsStatus.ERROR, error: action.payload };
    case REMOVED_POSTS_WEBSOCKET:
      return produce(state, draftState => {
        draftState.status = PostsStatus.REMOVED_WEBSOCKETS;
        draftState.data = castDraft(
          state.data.map(dt => {
            const newPosts = dt.posts.filter(post => post.id !== action.payload);
            return {
              ...dt,
              posts: newPosts,
            };
          })
        );
      });
    case UPDATE_POST_POLLING:
      return produce(state, draftState => {
        draftState.status = PostsStatus.UPDATE_POST_POLLING;
        draftState.data = castDraft(
          state.data.map((dt, i) => {
            const newPosts =
              i === 0
                ? handlePolledPosts(dt.posts, action.payload.posts, state.currentFilters)
                : dt.posts.filter(post => {
                    return action.payload.posts.filter(p => p.id === post.id).length < 1;
                  });
            return {
              ...dt,
              posts: newPosts,
            };
          })
        );
      });
    case NEW_POSTS_WEBSOCKET:
      return {
        ...state,
        data: [...state.data, action.payload],
        status: PostsStatus.NEW_POSTS_WEBSOCKETS,
      };
    case PUBLIC_POST_SUBMITTED:
      return {
        ...state,
        data: [action.payload, ...state.data],
        status: PostsStatus.PUBLIC_POST_SUBMITTED,
      };
    case APPROVE_POSTS_WEBSOCKET:
      return {
        ...state,
        data: [
          {
            posts: [action.payload.post],
            postsRelationships: action.payload.postsRelationships,
            links: action.payload.links,
          },
          ...state.data,
        ],
        status: PostsStatus.NEW_POSTS_WEBSOCKETS,
      };
    default:
      return state;
  }
}

export const postsRelationshipsSelector = (state: RootState): readonly IPostRelationships[] =>
  state.posts.data
    .filter(e => !!e.postsRelationships)
    .flatMap(e => e.postsRelationships as IPostRelationships[])
    .filter((relation, index, relationArray) => relationArray.findIndex(r => r.id === relation.id) === index);

export const postsSelector = (state: RootState) => removeDuplicateById(state.posts.data.flatMap(e => e.posts));
export const nextLinksSelector = (state: RootState) => state.posts.data.flatMap(e => e.links.next);
export const productsSelector = (state: RootState) => state.posts.products;
export const currentFiltersSelector = (state: RootState) => state.posts.currentFilters;

export const mergePostsWithCTAs = (posts: IPost[], postRelationships: readonly IPostRelationships[]) => {
  return posts.map(post => {
    return {
      ...post,
      cta: (postRelationships as ICtaAssociation[])
        .filter(relation => {
          return (
            post.relationships.cta_associations.data &&
            post.relationships.cta_associations.data.some(e => relation.id === e.id)
          );
        })
        .map(e => {
          const cta = postRelationships.find(
            relation =>
              e.relationships &&
              e.relationships.cta &&
              e.relationships.cta.data &&
              relation.id === e.relationships.cta.data.id
          ) as ICta;
          const { attributes } = e;
          return cta
            ? {
                id: cta.id,
                ...attributes,
                ...cta.attributes,
              }
            : null;
        })
        .filter(e => e),
    };
  });
};

const uniqWithExternalId = (posts: IPost[]): IPost[] => {
  return posts.filter((post, index, postsArray) => {
    // For those type we are checking duplicates in different method
    if (post?.attributes.type === `instagram_business`) {
      return true;
    }
    return (
      postsArray.findIndex(
        p =>
          p.attributes.external_id + p.attributes.attachment_id ===
            post.attributes.external_id + post.attributes.attachment_id && p.attributes.type === post.attributes.type
      ) === index
    );
  });
};

// Instagram only method
const uniqWithUrlAndExternalId = (posts: IPost[]): IPost[] => {
  return posts.filter((post, index, postsArray) => {
    if (post?.attributes.type === `instagram_business` && post?.attributes.media_type !== 'carousel') {
      return (
        postsArray.findIndex(
          p => p.attributes.url === post.attributes.url && post.attributes.external_id === p.attributes.external_id
        ) === index
      );
    }
    return true;
  });
};

const uniqueWithPhash = (posts: IPost[]): IPost[] => {
  return posts.filter(post => {
    if (post.attributes.metadata.image_phash && post?.attributes.media_type !== 'carousel') {
      const duplicatedArray = posts.filter(
        p =>
          p.attributes.metadata.image_phash === post.attributes.metadata.image_phash &&
          p.attributes.external_id + p.attributes.attachment_id ===
            post.attributes.external_id + post.attributes.attachment_id
      );
      return duplicatedArray.length === 1 || (duplicatedArray.length > 1 && duplicatedArray.indexOf(post) === 0);
    }
    return true;
  });
};

export const removeDuplicateById = (posts: readonly IPostWithCTA[] | readonly IPost[]) => {
  return posts.filter((post, index, postsArray) => postsArray.findIndex(p => p.id === post.id) === index);
};

const getUniqueItemById = list => {
  return list.filter((item, index, arr) => {
    return (
      arr
        .filter(() => item && item.id)
        .map(item => item.id)
        .indexOf(item.id) === index
    );
  });
};

const mapRelationshipsProducts = (productState: IProductsData, data?: readonly IPostRelationships[]) => {
  const productList = (data && data[0] && data.filter(item => item.type === 'product')) || [];
  const productTags = (data && data[0] && data.filter(item => item.type === 'product_tag')) || [];

  return {
    productList: getUniqueItemById([...productState.productList, ...productList]),
    productTags: getUniqueItemById([...productState.productTags, ...productTags]),
  };
};
